import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { toast } from 'react-toastify';
import CaisseController from '../controllers/caisse-controller';

const BASEURL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const isCurrentUserAdmin = () => {
  try {
    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
    return userData.isAdmin === true;
  } catch { return false; }
};

const CaisseClosureModal = ({ isOpen, onClose, caisseId = null }) => {
  const [step, setStep] = useState('init'); // 'init' | 'auth' | 'loading' | 'summary' | 'closed'
  const [adminUsername, setAdminUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [error, setError] = useState('');
  const [summaryData, setSummaryData] = useState(null);
  const [caisse, setCaisse] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const printRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      if (isCurrentUserAdmin()) {
        loadCaisseSummary();
      } else {
        setStep('auth');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const resetState = () => {
    setStep('init');
    setAdminUsername('');
    setPassword('');
    setError('');
    setSummaryData(null);
    setCaisse(null);
    setIsVerifying(false);
    setIsClosing(false);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  // Load caisse orders WITHOUT closing it
  const loadCaisseSummary = async () => {
    setStep('loading');
    setError('');

    try {
      let currentCaisse;
      try {
        if (caisseId) {
          // Closing a specific caisse from the list view
          currentCaisse = await CaisseController.getById(caisseId);
        } else {
          currentCaisse = await CaisseController.current();
        }
      } catch {
        currentCaisse = null;
      }

      if (!currentCaisse || !currentCaisse.id) {
        setError(caisseId ? 'Caisse introuvable.' : 'Aucune caisse ouverte actuellement.');
        setStep('auth');
        return;
      }
      if (currentCaisse.cloture) {
        setError('Cette caisse est déjà clôturée.');
        setStep('auth');
        return;
      }

      setCaisse(currentCaisse);

      // Fetch all orders for this caisse
      let allOrders = [];
      let page = 1;
      let maxPage = 1;

      do {
        const result = await CaisseController.paginateCommandes(currentCaisse.id, page, 200);
        if (result && result.list) {
          allOrders = [...allOrders, ...result.list];
          maxPage = result.maxPage || 1;
        } else {
          break;
        }
        page++;
      } while (page <= maxPage);

      // Build summary - compute the amount actually received on THIS caisse for each commande:
      //   - created here AND not finalized elsewhere (caisseId == this, caissePaiementId == null):
      //       full montantPaye (covers both pending preorders and same-caisse top-up payments)
      //   - created here BUT finalized elsewhere (caisseId == this, caissePaiementId != null):
      //       only the initial deposit (montantInitialPaye)
      //   - finalized here from another caisse (caissePaiementId == this):
      //       the remainder (montantPaye - montantInitialPaye)
      const validOrders = allOrders.filter(o => o.etat !== 'canceled');
      const enriched = validOrders.map((o) => {
        let received = 0;
        if (o.caisseId === currentCaisse.id) {
          if (o.caissePaiementId == null) received += o.montantPaye || 0;
          else received += o.montantInitialPaye || 0;
        }
        if (o.caissePaiementId === currentCaisse.id && o.caisseId !== currentCaisse.id) {
          received += (o.montantPaye || 0) - (o.montantInitialPaye || 0);
        }
        return { ...o, receivedHere: received };
      });
      const totalTTC = enriched.reduce((sum, o) => sum + o.receivedHere, 0);
      const fondCaisse = currentCaisse.fondCaisse || 0;
      const totalCaisse = totalTTC + fondCaisse;

      // Group by mode de paiement (using received-here amounts)
      const paiementMap = {};
      enriched.forEach(o => {
        if (o.receivedHere <= 0) return;
        const mode = o.modePaiement || 'Espèce';
        if (!paiementMap[mode]) paiementMap[mode] = { count: 0, total: 0 };
        paiementMap[mode].count++;
        paiementMap[mode].total += o.receivedHere;
      });
      const byPaiement = Object.entries(paiementMap)
        .map(([mode, data]) => ({ mode, ...data }))
        .sort((a, b) => b.total - a.total);

      // Précommandes (pending / not fully paid orders that touch this caisse)
      const preorders = enriched.filter(o => (o.etat === 'pending') || ((o.totalTTC || 0) > (o.montantPaye || 0) + 0.0001));
      const preorderTotal = preorders.reduce((s, o) => s + (o.totalTTC || 0), 0);
      const preorderAvance = preorders.reduce((s, o) => s + (o.receivedHere || 0), 0);
      const preorderReste = preorders.reduce((s, o) => s + Math.max(0, (o.totalTTC || 0) - (o.montantPaye || 0)), 0);

      // Fetch product references per order so the closure ticket can list them.
      const withLines = await Promise.all(enriched.map(async (o) => {
        try {
          const full = await CaisseController.getCommande(o.id);
          const lines = full?.line || [];
          const fmt = (l) => {
            const ref = l.product?.reference || l.productVariant?.reference || l.product?.name || '-';
            const variant = l.productVariant?.name ? ` (${l.productVariant.name})` : '';
            const qty = Math.abs(l.quantite || 1);
            const base = `${ref}${variant}`;
            return qty > 1 ? `${base} x${qty}` : base;
          };
          const isEchange = o.etat === 'echange';
          if (isEchange) {
            // In an exchange, returned products are stored with negative quantity,
            // newly sold products with positive quantity.
            const returnedRefs = lines.filter((l) => (l.quantite || 0) < 0).map(fmt);
            const soldRefs = lines.filter((l) => (l.quantite || 0) > 0).map(fmt);
            return { ...o, isEchange: true, returnedRefs, soldRefs, productRefs: [] };
          }
          const refs = lines.map(fmt);
          return { ...o, isEchange: false, productRefs: refs };
        } catch {
          return { ...o, isEchange: o.etat === 'echange', productRefs: [], returnedRefs: [], soldRefs: [] };
        }
      }));

      setSummaryData({
        dateDebut: currentCaisse.dateDebut ? new Date(currentCaisse.dateDebut) : new Date(),
        orders: withLines,
        totalTTC,
        fondCaisse,
        totalCaisse,
        byPaiement,
        nbCommandes: enriched.length,
        nbCanceled: allOrders.filter(o => o.etat === 'canceled').length,
        preorders: { count: preorders.length, total: preorderTotal, avance: preorderAvance, reste: preorderReste },
      });

      setStep('summary');
    } catch (err) {
      console.error('Load caisse error:', err);
      setError('Erreur lors du chargement de la caisse.');
      setStep('auth');
    }
  };

  // Verify admin credentials (username + password) then load summary
  const verifyPassword = async () => {
    if (!adminUsername || !password) {
      setError("Veuillez saisir le nom d'utilisateur et le mot de passe administrateur.");
      return;
    }

    setIsVerifying(true);
    setError('');

    try {
      const resp = await fetch(`${BASEURL}/Utilisateur/VerifyAdmin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ Username: adminUsername, password }),
      });

      if (!resp.ok) {
        let msg = 'Identifiants administrateur incorrects.';
        try { const data = await resp.json(); if (data?.message) msg = data.message; } catch { /* ignore */ }
        setError(msg);
        setIsVerifying(false);
        return;
      }

      setIsVerifying(false);
      await loadCaisseSummary();
    } catch (err) {
      console.error('Auth error:', err);
      setError('Erreur de connexion.');
      setIsVerifying(false);
    }
  };

  // Delete a commande from this caisse, restore its stock and recompute totals.
  const handleDeleteOrder = async (order) => {
    if (!order?.id) return;
    if (!window.confirm(`Supprimer la commande ${order.reference || '#' + order.id} ? Le stock sera restauré et le total recalculé.`)) return;
    setDeletingId(order.id);
    try {
      await CaisseController.deleteCommande(order.id);
      toast.success('Commande supprimée');
      await loadCaisseSummary();
    } catch (err) {
      console.error('Delete commande error:', err);
      toast.error(err?.response?.data?.message || 'Erreur lors de la suppression de la commande.');
    } finally {
      setDeletingId(null);
    }
  };

  // Actually close the caisse (only when user clicks Clôturer)
  const confirmCloseCaisse = async () => {
    if (!caisse) return;
    setIsClosing(true);
    try {
      await CaisseController.close(caisse.id);
      toast.success('Caisse clôturée avec succès !');
      setStep('closed');
    } catch (err) {
      console.error('Close caisse error:', err);
      toast.error('Erreur lors de la clôture de la caisse.');
    } finally {
      setIsClosing(false);
    }
  };

  const handlePrint = () => {
    if (!printRef.current) return;

    const content = printRef.current.innerHTML;
    const printHtml = `<!DOCTYPE html><html><head><title>Clôture Caisse</title>
      <style>
        @page { size: 80mm auto; margin: 3mm; }
        * { margin: 0; padding: 0; box-sizing: border-box; color:#000 !important; -webkit-print-color-adjust:exact; print-color-adjust:exact; }
        body { font-family: 'Arial Black','Helvetica',sans-serif !important; font-size: 15px !important; font-weight:700 !important; width: 74mm; padding: 2mm; color:#000 !important; line-height:1.4 !important; }
        body * { font-family: 'Arial Black','Helvetica',sans-serif !important; font-weight:700 !important; }
        h1 { font-size: 26px !important; font-weight: 900 !important; letter-spacing:1px; text-align:center; }
        h2 { font-size: 17px !important; font-weight: 900 !important; text-align:center; }
        h3, h4 { font-size: 16px !important; font-weight: 900 !important; }
        .ticket-header { text-align: center; margin-bottom: 8px; }
        .sep, hr { border-top: 2px dashed #000 !important; margin: 8px 0 !important; border-bottom:none !important; border-left:none !important; border-right:none !important; }
        .sep-bold { border-top: 3px solid #000 !important; margin: 8px 0 !important; }
        .line { display: flex; justify-content: space-between; padding: 3px 0; font-size: 15px !important; font-weight:700 !important; }
        .line *, .line span { font-size: 15px !important; font-weight:700 !important; }
        .line-big { display: flex; justify-content: space-between; padding: 5px 0; font-size: 20px !important; font-weight: 900 !important; }
        .line-big *, .line-big span { font-size: 20px !important; font-weight: 900 !important; }
        .section-title { text-align: center; font-weight: 900 !important; font-size: 16px !important; padding: 5px 0; }
        table { width: 100%; border-collapse: collapse; margin: 4px 0; }
        th { font-size: 14px !important; font-weight:900 !important; text-align: left; padding: 4px 3px !important; border-bottom: 2px solid #000 !important; }
        td { font-size: 14px !important; font-weight:700 !important; padding: 4px 3px !important; border-bottom: 1px solid #000 !important; }
        .text-right { text-align: right; }
        .footer { text-align: center; font-size: 14px !important; font-weight:900 !important; margin-top: 10px; }
        .no-print { display: none !important; }
      </style></head><body>${content}
    </body></html>`;

    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    iframe.setAttribute('aria-hidden', 'true');
    document.body.appendChild(iframe);
    const cleanup = () => { try { document.body.removeChild(iframe); } catch (e) {} };
    iframe.onload = () => {
      try {
        const w = iframe.contentWindow;
        setTimeout(() => {
          try { w.focus(); w.print(); } catch (e) { console.error(e); }
          w.onafterprint = () => setTimeout(cleanup, 200);
          setTimeout(cleanup, 60000);
        }, 150);
      } catch (e) { console.error(e); cleanup(); }
    };
    const doc = iframe.contentDocument || iframe.contentWindow.document;
    doc.open();
    doc.write(printHtml);
    doc.close();
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[9999] overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 py-6">
        <div className="fixed inset-0 bg-black bg-opacity-60 transition-opacity" onClick={handleClose} />

        <div className={`relative bg-white rounded-2xl shadow-2xl z-10 w-full transition-all ${step === 'summary' || step === 'closed' ? 'max-w-2xl' : 'max-w-md'}`}>

          {/* Header */}
          <div className="bg-gradient-to-r from-red-600 to-red-700 text-white px-6 py-4 rounded-t-2xl flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <h3 className="text-lg font-bold">
                {step === 'closed' ? 'Caisse Clôturée' : step === 'summary' ? 'Résumé de Caisse' : 'Fermer la Caisse'}
              </h3>
            </div>
            <button onClick={handleClose} className="text-white/80 hover:text-white transition">
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Auth Step */}
          {step === 'auth' && (
            <div className="p-6">
              <p className="text-sm text-gray-500 mb-4">Saisissez le nom d'utilisateur et le mot de passe administrateur pour accéder au résumé de la caisse.</p>
              <div className="mb-3">
                <input
                  type="text"
                  value={adminUsername}
                  onChange={e => setAdminUsername(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && verifyPassword()}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                  placeholder="Nom d'utilisateur administrateur"
                  autoFocus
                  autoComplete="username"
                />
              </div>
              <div className="mb-4">
                <input
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && verifyPassword()}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                  placeholder="Mot de passe administrateur"
                  autoComplete="current-password"
                />
              </div>
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">{error}</div>
              )}
              <div className="flex space-x-3">
                <button onClick={handleClose} className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50">
                  Annuler
                </button>
                <button
                  onClick={verifyPassword}
                  disabled={isVerifying}
                  className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50 flex items-center justify-center"
                >
                  {isVerifying ? (
                    <><svg className="animate-spin mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>Vérification...</>
                  ) : 'Continuer'}
                </button>
              </div>
            </div>
          )}

          {/* Loading Step */}
          {step === 'loading' && (
            <div className="p-10 flex flex-col items-center">
              <svg className="animate-spin h-10 w-10 text-red-600 mb-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
              </svg>
              <p className="text-gray-600 text-sm">Chargement du résumé...</p>
              {error && <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">{error}</div>}
            </div>
          )}

          {/* Summary Step (caisse NOT yet closed) */}
          {(step === 'summary' || step === 'closed') && summaryData && (
            <div className="max-h-[75vh] overflow-y-auto">
              {/* Printable ticket content */}
              <div ref={printRef} className="p-6">
                <div className="ticket-header" style={{ textAlign: 'center', marginBottom: '12px' }}>
                  <h1 style={{ fontSize: '20px', fontWeight: 'bold', margin: 0 }}>TENDRESSE</h1>
                  <h2 style={{ fontSize: '14px', fontWeight: 'normal', color: '#555', margin: '2px 0 0' }}>
                    {step === 'closed' ? 'CAISSE CLÔTURÉE' : 'RÉSUMÉ DE CAISSE'}
                  </h2>
                </div>

                <div className="sep" style={{ borderTop: '1px dashed #999', margin: '10px 0' }}></div>

                <div style={{ fontSize: '12px', lineHeight: '1.8' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Ouverture:</span>
                    <span style={{ fontWeight: 'bold' }}>{summaryData.dateDebut.toLocaleDateString('fr-FR')} {summaryData.dateDebut.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</span>
                  </div>
                  {step === 'closed' && (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>Clôture:</span>
                      <span style={{ fontWeight: 'bold' }}>{new Date().toLocaleDateString('fr-FR')} {new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                  )}
                </div>

                <div className="sep-bold" style={{ borderTop: '2px solid #333', margin: '10px 0' }}></div>

                {/* Summary numbers */}
                <div style={{ fontSize: '13px', lineHeight: '2' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Commandes:</span>
                    <span style={{ fontWeight: 'bold' }}>{summaryData.nbCommandes}</span>
                  </div>
                  {summaryData.nbCanceled > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', color: '#dc2626' }}>
                      <span>Annulées:</span>
                      <span>{summaryData.nbCanceled}</span>
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Encaissé dans cette caisse:</span>
                    <span style={{ fontWeight: 'bold' }}>{summaryData.totalTTC.toFixed(3)} TND</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Fond de caisse:</span>
                    <span style={{ fontWeight: 'bold' }}>{summaryData.fondCaisse.toFixed(3)} TND</span>
                  </div>
                </div>

                <div className="sep-bold" style={{ borderTop: '2px solid #000', margin: '10px 0' }}></div>

                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '16px', fontWeight: 'bold', padding: '4px 0' }}>
                  <span>TOTAL CAISSE:</span>
                  <span>{summaryData.totalCaisse.toFixed(3)} TND</span>
                </div>

                {/* By payment mode */}
                {summaryData.byPaiement.length > 0 && (
                  <>
                    <div className="sep" style={{ borderTop: '1px dashed #999', margin: '10px 0' }}></div>
                    <div style={{ textAlign: 'center', fontWeight: 'bold', fontSize: '12px', padding: '4px 0' }}>PAR MODE DE PAIEMENT</div>
                    <div style={{ fontSize: '12px', lineHeight: '1.8' }}>
                      {summaryData.byPaiement.map((p, i) => (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span>{p.mode} ({p.count})</span>
                          <span>{p.total.toFixed(3)} TND</span>
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {/* Précommandes */}
                {summaryData.preorders && summaryData.preorders.count > 0 && (
                  <>
                    <div className="sep" style={{ borderTop: '1px dashed #999', margin: '10px 0' }}></div>
                    <div style={{ textAlign: 'center', fontWeight: 'bold', fontSize: '12px', padding: '4px 0' }}>PRECOMMANDES</div>
                    <div style={{ fontSize: '12px', lineHeight: '1.8' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Nombre:</span><span style={{ fontWeight: 'bold' }}>{summaryData.preorders.count}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Montant total:</span><span style={{ fontWeight: 'bold' }}>{summaryData.preorders.total.toFixed(3)} TND</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Avance encaissée:</span><span style={{ fontWeight: 'bold' }}>{summaryData.preorders.avance.toFixed(3)} TND</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Reste à encaisser:</span><span style={{ fontWeight: 'bold' }}>{summaryData.preorders.reste.toFixed(3)} TND</span>
                      </div>
                    </div>
                  </>
                )}

                {/* Order detail list */}
                {summaryData.orders.length > 0 && (
                  <>
                    <div className="sep" style={{ borderTop: '1px dashed #999', margin: '10px 0' }}></div>
                    <div style={{ textAlign: 'center', fontWeight: 'bold', fontSize: '12px', padding: '4px 0' }}>DÉTAIL DES COMMANDES</div>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px', marginTop: '4px' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid #333' }}>
                          <th style={{ textAlign: 'left', padding: '4px', fontWeight: 'bold' }}>#</th>
                          <th style={{ textAlign: 'left', padding: '4px', fontWeight: 'bold' }}>Réf</th>
                          <th style={{ textAlign: 'right', padding: '4px', fontWeight: 'bold' }}>Total</th>
                          <th style={{ textAlign: 'right', padding: '4px', fontWeight: 'bold' }}>Reçu</th>
                          {step === 'summary' && <th className="no-print" style={{ width: 28 }}></th>}
                        </tr>
                      </thead>
                      <tbody>
                        {summaryData.orders.map((o, i) => {
                          const isPre = (o.etat === 'pending') || ((o.totalTTC || 0) > (o.montantPaye || 0) + 0.0001);
                          const isEch = o.isEchange || o.etat === 'echange';
                          const refs = (o.productRefs || []).join(', ');
                          const returnedRefs = (o.returnedRefs || []).join(', ');
                          const soldRefs = (o.soldRefs || []).join(', ');
                          const client = `${o.clientFirstName || ''} ${o.clientLastName || ''}`.trim();
                          return (
                            <React.Fragment key={o.id || i}>
                              <tr>
                                <td style={{ padding: '3px 4px' }}>{i + 1}</td>
                                <td style={{ padding: '3px 4px' }}>
                                  {o.reference || '-'}
                                  {isEch && <span style={{ marginLeft: 4, fontSize: 10, fontWeight: 'bold' }}>[ECHANGE]</span>}
                                  {!isEch && isPre && <span style={{ marginLeft: 4, fontSize: 10, fontWeight: 'bold' }}>[PRECO]</span>}
                                </td>
                                <td style={{ padding: '3px 4px', textAlign: 'right' }}>{(o.totalTTC || 0).toFixed(3)}</td>
                                <td style={{ padding: '3px 4px', textAlign: 'right', fontWeight: 'bold' }}>{(o.receivedHere || 0).toFixed(3)}</td>
                                {step === 'summary' && (
                                  <td className="no-print" style={{ padding: '3px 4px', textAlign: 'center' }}>
                                    <button
                                      type="button"
                                      onClick={() => handleDeleteOrder(o)}
                                      disabled={deletingId === o.id}
                                      title="Supprimer cette commande"
                                      className="text-red-600 hover:text-red-800 disabled:opacity-40"
                                    >
                                      {deletingId === o.id ? (
                                        <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                                      ) : (
                                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                      )}
                                    </button>
                                  </td>
                                )}
                              </tr>
                              {isEch ? (
                                <tr>
                                  <td></td>
                                  <td colSpan={3} style={{ fontSize: 10, padding: '0 4px 4px', borderBottom: '1px dotted #ccc' }}>
                                    {client && <div>{client}</div>}
                                    {returnedRefs && <div style={{ color: '#444' }}><span style={{ fontWeight: 'bold' }}>Retour:</span> {returnedRefs}</div>}
                                    {soldRefs && <div style={{ color: '#444' }}><span style={{ fontWeight: 'bold' }}>Vendu:</span> {soldRefs}</div>}
                                  </td>
                                </tr>
                              ) : (
                                (client || refs) && (
                                  <tr>
                                    <td></td>
                                    <td colSpan={3} style={{ fontSize: 10, padding: '0 4px 4px', borderBottom: '1px dotted #ccc' }}>
                                      {client && <div>{client}</div>}
                                      {refs && <div style={{ color: '#444' }}>{refs}</div>}
                                    </td>
                                  </tr>
                                )
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </>
                )}

                <div className="sep" style={{ borderTop: '1px dashed #999', margin: '10px 0' }}></div>
                <div style={{ textAlign: 'center', fontSize: '10px', color: '#666', padding: '4px 0' }}>
                  Merci — Tendresse
                </div>
              </div>

              {/* Action buttons */}
              <div className="sticky bottom-0 bg-white border-t px-6 py-4 flex space-x-3">
                {step === 'summary' && (
                  <>
                    <button
                      onClick={handleClose}
                      className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      Annuler
                    </button>
                    <button
                      onClick={confirmCloseCaisse}
                      disabled={isClosing}
                      className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50 flex items-center justify-center"
                    >
                      {isClosing ? (
                        <><svg className="animate-spin mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>Clôture...</>
                      ) : (
                        <><svg className="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/></svg>Clôturer la Caisse</>
                      )}
                    </button>
                  </>
                )}
                {step === 'closed' && (
                  <>
                    <button
                      onClick={handleClose}
                      className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      Fermer
                    </button>
                    <button
                      onClick={handlePrint}
                      className="flex-1 px-4 py-2.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 flex items-center justify-center"
                    >
                      <svg className="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                      </svg>
                      Imprimer le Ticket
                    </button>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CaisseClosureModal;
